import type { Category, CookieWarningLanguage, Customer, EventItem, FrontpageSegment, Promoted, SpecialCategory, Venue } from '@/types';
import { mapDtosToCategories } from '@/utils/dtoTransformers/categoryDTO';
import { mapDtoToCustomer } from '@/utils/dtoTransformers/customerDTO';
import { mapDtosToEventItems } from '@/utils/dtoTransformers/eventItemDTO';
import { mapDtosToPromoted } from '@/utils/dtoTransformers/promotedDTO';
import { mapDtosToSpecialCategories } from '@/utils/dtoTransformers/specialCategoryDTO';
import { mapDtosToVenues } from '@/utils/dtoTransformers/venueDTO';

export interface GeneralLists {
  events: EventItem[];
  moreEvents: EventItem[];
  venues: Venue[];
  categories: Category[];
  customer: Customer;
  promoted: Promoted[];
  frontpage2_segments: FrontpageSegment[];
  special_categories: SpecialCategory[];
  languages: string[];
  short_urls: Record<string, number>;
  cookie_warning: Record<string, CookieWarningLanguage>;
  rev: number | undefined;
}

export async function fetchGeneralLists(): Promise<GeneralLists> {
  let events: EventItem[] = [];
  let moreEvents: EventItem[] = [];
  let venues: Venue[] = [];
  let categories: Category[] = [];
  let customer: Customer = {} as Customer;
  let promoted: Promoted[] = [];
  let frontpage2_segments: FrontpageSegment[] = [];
  let special_categories: SpecialCategory[] = [];
  let languages: string[] = [];
  let short_urls: Record<string, number> = {};
  let cookie_warning: Record<string, CookieWarningLanguage> = {};
  let rev: number | undefined = undefined;

  try {
    const response = await fetch('/of2/json/general', {
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      }
    });
    const data = await response.json();
    // console.log("Fetched general lists data:", data);

    events = Array.isArray(data.events) ? mapDtosToEventItems(data.events) : [];
    moreEvents = [];
    const rawVenues = Array.isArray(data.venues) ? data.venues : Object.values(data.venues ?? {});
    venues = mapDtosToVenues(rawVenues);
    categories = Array.isArray(data.categories) ? mapDtosToCategories(data.categories) : [];
    special_categories = Array.isArray(data.special_categories) ? mapDtosToSpecialCategories(data.special_categories) : [];
    customer = mapDtoToCustomer(data.customer);
    promoted = Array.isArray(data.promoted) ? mapDtosToPromoted(data.promoted) : [];
    frontpage2_segments = Array.isArray(data.frontpage2_segments) ? data.frontpage2_segments : [];
    languages = Array.isArray(data.languages) ? data.languages : [];
    short_urls = data.short_urls !== null ? data.short_urls : {};
    cookie_warning = (data.cookie_warning != null && typeof data.cookie_warning === 'object') ? data.cookie_warning : {};
    rev = typeof data.rev === 'number' ? data.rev : undefined;
  } catch (e) {
    console.error("Failed to fetch general lists:", e);
  }

  return {
    events,
    moreEvents,
    venues,
    categories,
    customer,
    promoted: promoted || [],
    frontpage2_segments,
    special_categories,
    languages,
    short_urls,
    cookie_warning,
    rev,
  };
}